import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Clock,
  Settings,
  Calendar,
  CreditCard,
  Stethoscope,
  ExternalLink,
  RefreshCw,
  Mail,
  Smartphone,
  MessageSquare,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  notificationsApi,
  type NotificationItem,
  type NotificationPreference,
} from '@/api/notifications-api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'UNREAD'>('ALL');
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const limit = 10;

  // 1. Query das Notificações
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['patient', 'notifications', page, filter],
    queryFn: async () => {
      const response = await notificationsApi.getNotifications({
        page,
        limit,
        status: filter === 'UNREAD' ? 'UNREAD' : undefined,
      });
      return {
        items: response?.items || [],
        total: response?.total || 0,
      };
    },
  });

  // 2. Query para o contador total de não lidas
  const { data: unreadData } = useQuery({
    queryKey: ['patient', 'notifications', 'unread-count'],
    queryFn: async () => {
      const res = await notificationsApi.getNotifications({
        page: 1,
        limit: 1,
        status: 'UNREAD',
      });
      return res?.total || 0;
    },
    staleTime: 1000 * 30, // 30s
  });

  // 3. Query das Preferências do Usuário
  const { data: preferences, refetch: refetchPreferences } = useQuery<NotificationPreference | null>({
    queryKey: ['patient', 'notification-preferences'],
    queryFn: () => notificationsApi.getPreferences(),
    enabled: isPreferencesOpen,
  });

  // Mutações
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'notifications'] });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'notifications'] });
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (updated: Partial<NotificationPreference>) =>
      notificationsApi.updatePreferences(updated),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'notification-preferences'] });
      refetchPreferences();
      setIsPreferencesOpen(false);
    },
  });

  const notifications = data?.items || [];
  const unreadTotal = unreadData ?? 0;
  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

      if (diffHours < 1) return 'Agora mesmo';
      if (diffHours < 24) return `${diffHours}h atrás`;
      if (diffHours < 48) return 'Ontem';
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return '--';
    }
  };

  const getNotificationCategory = (item: NotificationItem) => {
    const titleLower = item.title.toLowerCase();
    const dataObj = (item.data as Record<string, any>) || {};
    const type = dataObj.type?.toLowerCase() || '';

    if (titleLower.includes('consulta') || titleLower.includes('agendamento') || type.includes('appointment')) {
      return {
        icon: Calendar,
        bg: 'bg-primary/10 text-primary border-primary/20',
        label: 'Consulta',
      };
    }
    if (titleLower.includes('pagamento') || titleLower.includes('pix') || titleLower.includes('saque') || type.includes('payment')) {
      return {
        icon: CreditCard,
        bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
        label: 'Financeiro',
      };
    }
    if (titleLower.includes('médic') || titleLower.includes('receita') || titleLower.includes('atestado') || titleLower.includes('exame')) {
      return {
        icon: Stethoscope,
        bg: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
        label: 'Saúde',
      };
    }
    if (titleLower.includes('indique') || titleLower.includes('parceiro') || titleLower.includes('comissão')) {
      return {
        icon: Sparkles,
        bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
        label: 'Indique e Ganhe',
      };
    }
    return {
      icon: Bell,
      bg: 'bg-muted text-foreground border-border',
      label: 'Sistema',
    };
  };

  const handleNotificationClick = (item: NotificationItem) => {
    const isUnread = item.status !== 'READ' && !item.readAt;
    if (isUnread) {
      markAsReadMutation.mutate(item.id);
    }

    const dataObj = (item.data as Record<string, any>) || {};
    const targetUrl = dataObj.actionUrl || dataObj.url || dataObj.link;

    if (targetUrl) {
      if (targetUrl.startsWith('http')) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(targetUrl);
      }
      return;
    }

    // Navegação inteligente inferida
    if (dataObj.appointmentId) {
      navigate(`/agendamentos/${dataObj.appointmentId}`);
    } else if (dataObj.prescriptionId) {
      navigate('/receitas');
    } else if (dataObj.examRequestId || dataObj.examId) {
      navigate('/exames');
    } else if (dataObj.partnerId) {
      navigate('/parceiro');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Notificações
            </h1>
            {unreadTotal > 0 && (
              <Badge
                variant="outline"
                className="font-bold text-xs bg-destructive/10 text-destructive border-destructive/20 animate-pulse"
              >
                {unreadTotal} não {unreadTotal === 1 ? 'lida' : 'lidas'}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Acompanhe avisos de consultas, receitas digitais, pagamentos e atualizações do sistema
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {unreadTotal > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
              className="gap-1.5 rounded-xl font-semibold shadow-xs hover:bg-muted text-xs h-11 flex-1 sm:flex-initial"
            >
              <CheckCheck className="h-4 w-4 text-primary" />
              <span>Marcar todas como lidas</span>
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-xl h-11 w-11 p-0 flex items-center justify-center hover:bg-muted"
            title="Atualizar notificações"
            aria-label="Atualizar notificações"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-primary' : ''}`} />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPreferencesOpen(true)}
            className="rounded-xl h-11 w-11 p-0 flex items-center justify-center hover:bg-muted"
            title="Preferências de Notificação"
            aria-label="Preferências de Notificação"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Filtros em Pílula */}
      <div className="flex items-center gap-2 bg-muted/40 p-1.5 rounded-2xl w-fit border border-border/60">
        <button
          type="button"
          onClick={() => {
            setFilter('ALL');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
            filter === 'ALL'
              ? 'bg-card text-foreground shadow-xs border border-border/80'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Todas
        </button>
        <button
          type="button"
          onClick={() => {
            setFilter('UNREAD');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
            filter === 'UNREAD'
              ? 'bg-card text-foreground shadow-xs border border-border/80'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span>Não lidas</span>
          {unreadTotal > 0 && (
            <span className="w-2 h-2 rounded-full bg-primary" />
          )}
        </button>
      </div>

      {/* Estados de Carregamento, Erro ou Vazio */}
      {isLoading ? (
        <LoadingOverlay message="Carregando notificações..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar notificações" onRetry={() => refetch()} />
      ) : notifications.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4 shadow-xs">
            <Bell className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {filter === 'UNREAD' ? 'Nenhuma notificação não lida' : 'Nenhuma notificação encontrada'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {filter === 'UNREAD'
              ? 'Todas as suas notificações foram visualizadas. Quando houver novidades, você será alertado.'
              : 'Você receberá notificações instantâneas sobre agendamentos, chamadas de vídeo, novas receitas e pagamentos.'}
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setFilter('ALL');
              refetch();
            }}
            className="rounded-xl font-semibold gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Atualizar Painel</span>
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const isRead = notification.status === 'READ' || Boolean(notification.readAt);
            const category = getNotificationCategory(notification);
            const CategoryIcon = category.icon;
            const dataObj = (notification.data as Record<string, any>) || {};
            const hasAction = Boolean(
              dataObj.actionUrl ||
              dataObj.url ||
              dataObj.link ||
              dataObj.appointmentId ||
              dataObj.prescriptionId ||
              dataObj.examRequestId
            );

            return (
              <Card
                key={notification.id}
                className={`rounded-2xl border transition-all duration-200 shadow-xs overflow-hidden ${
                  !isRead
                    ? 'border-primary/40 bg-primary/[0.02] dark:bg-primary/[0.04] shadow-sm'
                    : 'border-border bg-card opacity-90 hover:opacity-100'
                }`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3.5">
                    {/* Ícone de Categoria */}
                    <div
                      className={`w-11 h-11 rounded-2xl border flex items-center justify-center shrink-0 shadow-xs ${category.bg}`}
                    >
                      <CategoryIcon className="w-5 h-5" />
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className={hasAction ? 'cursor-pointer group' : ''}
                          onClick={() => hasAction && handleNotificationClick(notification)}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <h3
                              className={`text-sm sm:text-base font-bold tracking-tight text-foreground ${
                                hasAction ? 'group-hover:text-primary transition-colors' : ''
                              }`}
                            >
                              {notification.title}
                            </h3>
                            {!isRead && (
                              <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-line">
                            {notification.message}
                          </p>
                        </div>

                        {/* Ações Rápidas */}
                        <div className="flex items-center gap-1 shrink-0">
                          {!isRead && (
                            <button
                              type="button"
                              onClick={() => markAsReadMutation.mutate(notification.id)}
                              className="p-2 text-muted-foreground hover:text-primary hover:bg-muted/70 rounded-xl transition-colors cursor-pointer"
                              title="Marcar como lida"
                              aria-label="Marcar como lida"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteNotificationMutation.mutate(notification.id)}
                            className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors cursor-pointer"
                            title="Excluir notificação"
                            aria-label="Excluir notificação"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Rodapé da Notificação */}
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-border/40 text-[11px] sm:text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            {formatDate(notification.createdAt)}
                          </span>

                          <Badge variant="outline" className="text-[10px] font-semibold">
                            {category.label}
                          </Badge>
                        </div>

                        {hasAction && (
                          <button
                            type="button"
                            onClick={() => handleNotificationClick(notification)}
                            className="font-bold text-primary hover:underline flex items-center gap-1 cursor-pointer ml-auto"
                          >
                            <span>Acessar detalhes</span>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-xl font-medium"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="px-3.5 py-1.5 bg-primary/10 text-primary font-bold text-xs rounded-xl border border-primary/20">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-xl font-medium"
          >
            Próximo
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Modal de Preferências de Notificação */}
      <Dialog open={isPreferencesOpen} onOpenChange={setIsPreferencesOpen}>
        <DialogContent className="max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
          <DialogHeader className="mb-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2 shadow-xs">
              <Settings className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
              Canais de Notificação
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Escolha por quais canais você deseja receber lembretes e alertas da Medikei.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm text-foreground">
            <label className="flex items-center justify-between p-3.5 rounded-xl border border-border/80 bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-primary" />
                <div>
                  <span className="font-semibold block">Notificações no App</span>
                  <span className="text-xs text-muted-foreground">Alertas dentro do portal</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences?.inAppEnabled ?? true}
                onChange={(e) =>
                  updatePreferencesMutation.mutate({ inAppEnabled: e.target.checked })
                }
                className="w-4 h-4 rounded text-primary focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-border/80 bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary" />
                <div>
                  <span className="font-semibold block">E-mail</span>
                  <span className="text-xs text-muted-foreground">Confirmações e relatórios</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences?.emailEnabled ?? true}
                onChange={(e) =>
                  updatePreferencesMutation.mutate({ emailEnabled: e.target.checked })
                }
                className="w-4 h-4 rounded text-primary focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-border/80 bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-emerald-500" />
                <div>
                  <span className="font-semibold block">WhatsApp</span>
                  <span className="text-xs text-muted-foreground">Avisos de consultas e links diretos</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences?.whatsappEnabled ?? true}
                onChange={(e) =>
                  updatePreferencesMutation.mutate({ whatsappEnabled: e.target.checked })
                }
                className="w-4 h-4 rounded text-primary focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-border/80 bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-amber-500" />
                <div>
                  <span className="font-semibold block">Lembretes de Consulta</span>
                  <span className="text-xs text-muted-foreground">Alertas 2h e 15min antes</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences?.appointmentReminders ?? true}
                onChange={(e) =>
                  updatePreferencesMutation.mutate({ appointmentReminders: e.target.checked })
                }
                className="w-4 h-4 rounded text-primary focus:ring-primary"
              />
            </label>
          </div>

          <DialogFooter className="mt-4 pt-3 border-t border-border">
            <Button
              type="button"
              onClick={() => setIsPreferencesOpen(false)}
              className="w-full rounded-xl font-bold"
            >
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
